import {
  EFEX_PRODUCTS_OPTIONS,
  FUNDS_SOURCE_OPTIONS,
  PAYMENTS_AMOUNT_OPTIONS,
  PAYMENTS_FREQUENCY_OPTIONS_TRANSACTIONAL,
} from '@efex-components/common/Form/config';
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { Button } from '@efexteam/components';
import { captureException } from '@sentry/react';
import {
  CONTACT_METHODS_LINK,
  CONTACT_METHODS_VALUES,
} from '@efex-components/payables/PaymentModal/constants/catalogs';
import clsx from 'clsx';
import useTransactionalProfileService from './hooks/useTransactionalProfileService';
import styles from './TransactionalProfile.style';
import getOriginalKeysFromTranslations from './utils/translationMapper';

type FormData = {
  goodsOffered: string;
  businessPromotion: string;
  depositMonthlyVolume: string;
  depositMonthlyAmount: string;
  withdrawalMonthlyVolume: string;
  withdrawalMonthlyAmount: string;
  fundsSource: string;
  accountUsage: string;
  transactionsAbroad: boolean;
  efexProductUsage: string;
};

interface TransactionalProfileProps {
  loading?: boolean;
  nextFormStep?: (values: any) => void;
  prevStep?: () => void;
  isDisabledEdit?: boolean;
}

const TransactionalProfile = ({
  loading: externalLoading,
  nextFormStep,
  prevStep,
  isDisabledEdit = false,
}: TransactionalProfileProps) => {
  const { t, i18n } = useTranslation();
  const {
    loading: serviceLoading,
    error,
    updateTransactionProfile,
    existingProfileData,
    errorUpdate,
    transactionalProfileData,
    loadingUpdate,
  } = useTransactionalProfileService();

  const loading = externalLoading || serviceLoading;

  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
    reset,
  } = useForm<FormData>({ mode: 'all' });

  const currentLanguage = useMemo(
    () => i18n.language.toUpperCase() as 'EN' | 'ES',
    [i18n.language]
  );

  useEffect(() => {
    if (existingProfileData) {
      const productUsageTranslations = existingProfileData.efexProductUsage
        ?.split(', ')
        .map((v) => v.trim());

      const originalKeys = productUsageTranslations
        ? getOriginalKeysFromTranslations(productUsageTranslations)
        : [];

      const updatedData = {
        ...existingProfileData,
        efexProductUsage: originalKeys.join(', '),
      };

      reset(updatedData);
    }
  }, [existingProfileData, reset, currentLanguage]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      const efexProductUsage = values.efexProductUsage
        .split(', ')
        .map((v) => v.trim());
      const efexProductUsageValues = efexProductUsage.map((v) => t(v));

      values.efexProductUsage = efexProductUsageValues.join(', ');

      updateTransactionProfile(values);
    } catch (err) {
      captureException(err);
    }
  });

  useEffect(() => {
    if (loadingUpdate) return;

    if (errorUpdate) {
      captureException(errorUpdate);
      return;
    }

    if (transactionalProfileData && !loadingUpdate && nextFormStep)
      nextFormStep({ isLastStep: false });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorUpdate, transactionalProfileData, loadingUpdate]);

  const renderError = (errorMessage?: string) => (
    <p className="text-orange-600 text-xs">
      {t(errorMessage || 'defaultError')}
    </p>
  );

  return (
    <Box className={styles.wrapper}>
      <form onSubmit={onSubmit}>
        <Typography variant="h4" className="!font-medium">
          {t('tpTitle')}
        </Typography>
        <Typography className="!leading-5 !mt-[10px] !mb-[20px]">
          {t('tpSubtitle')}
        </Typography>
        <Grid container xs={12} className="max-w-full overflow-hidden">
          <FormControl fullWidth className="!mb-6">
            <Typography className="!mb-2">{t('tpGoodsServices')}</Typography>
            <TextField
              id="goodsOffered"
              placeholder={t('tpGoodsServicesPlaceholder') ?? ''}
              error={!!errors.goodsOffered}
              {...register('goodsOffered', {
                required: true,
                maxLength: 100,
              })}
            />
            {errors.goodsOffered &&
              errors.goodsOffered.type === 'required' &&
              renderError()}
            {errors.goodsOffered &&
              errors.goodsOffered.type === 'maxLength' &&
              renderError('maxLength100Characters')}
          </FormControl>
          <FormControl fullWidth className="!mb-6">
            <Typography className="!mb-2">
              {t('tpBusinessPromotion')}
            </Typography>
            <TextField
              id="businessPromotion"
              placeholder={t('tpBusinessPromotionPlaceholder') ?? ''}
              error={!!errors.businessPromotion}
              {...register('businessPromotion', {
                required: true,
                maxLength: 100,
              })}
            />
            {errors.businessPromotion &&
              errors.businessPromotion.type === 'required' &&
              renderError()}
            {errors.businessPromotion &&
              errors.businessPromotion.type === 'maxLength' &&
              renderError('maxLength100Characters')}
          </FormControl>
          <FormControl fullWidth className="!mb-6">
            <Typography className="!mb-2">{t('tpFundsSource')}</Typography>
            <Controller
              name="fundsSource"
              rules={{ required: true }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  select
                  id="fundsSource"
                  error={!!errors.fundsSource}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) => {
                      if (!selected) {
                        return (
                          <em className="text-gray-400">
                            {t('tpFundsSourcePlaceholder')}
                          </em>
                        );
                      }
                      return selected;
                    },
                  }}
                  value={value ?? ''}
                  onChange={onChange}
                >
                  {FUNDS_SOURCE_OPTIONS?.map((fundsSource) => (
                    <MenuItem key={fundsSource.id} value={t(fundsSource.value)}>
                      {t(fundsSource.value)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {errors.fundsSource &&
              errors.fundsSource.type === 'required' &&
              renderError()}
            {errors.fundsSource &&
              errors.fundsSource.type === 'maxLength' &&
              renderError('maxLength100Characters')}
          </FormControl>
          <FormControl fullWidth className="!mb-6">
            <Typography className="!mb-2">{t('tpAccountUsage')}</Typography>
            <TextField
              id="accountUsage"
              placeholder={t('tpAccountUsagePlaceholder') ?? ''}
              error={!!errors.accountUsage}
              {...register('accountUsage', {
                required: true,
                maxLength: 100,
              })}
            />
            {errors.accountUsage &&
              errors.accountUsage.type === 'required' &&
              renderError()}
            {errors.accountUsage &&
              errors.accountUsage.type === 'maxLength' &&
              renderError('maxLength100Characters')}
          </FormControl>

          <FormControl fullWidth className="!mb-4">
            <Typography className="!mb-2">{t('tpProducts')}</Typography>
            <Controller
              name="efexProductUsage"
              rules={{ required: true }}
              control={control}
              render={({ field: { onChange, value } }) => {
                const selectedValues = value
                  ? value.split(',').map((v) => v.trim())
                  : [];

                return (
                  <Select
                    multiple
                    value={selectedValues}
                    input={<OutlinedInput label={t(`onbFinalQuestions3`)} />}
                    renderValue={(selected) =>
                      selected
                        .map((selectedValue) => {
                          const option = EFEX_PRODUCTS_OPTIONS?.find(
                            (product) => product.value === selectedValue
                          );
                          return option ? t(option.value) : selectedValue;
                        })
                        .join(', ')
                    }
                    className={clsx(
                      'w-full h-12 border border-solid border-gray-300 rounded-md text-base',
                      errors.efexProductUsage && 'border-red-500'
                    )}
                    onChange={(event) => {
                      const selectedArray = event.target.value as string[];
                      const stringValue = selectedArray.join(', ');
                      onChange(stringValue);
                    }}
                  >
                    {EFEX_PRODUCTS_OPTIONS?.map((product) => (
                      <MenuItem key={product.id} value={product.value}>
                        <Checkbox
                          checked={selectedValues.includes(product.value)}
                        />
                        <ListItemText primary={t(product.value)} />
                      </MenuItem>
                    ))}
                  </Select>
                );
              }}
            />
            {errors.efexProductUsage && renderError()}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography className="!font-medium !mb-4" variant="h6">
            {t('tpIncome')}
          </Typography>
          <FormControl fullWidth className="!mb-4">
            <Typography className="!mb-2">
              {t('tpTransactionsPerMonth')}
            </Typography>
            <Controller
              name="withdrawalMonthlyVolume"
              rules={{ required: true }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  select
                  id="withdrawalMonthlyVolume"
                  error={!!errors.withdrawalMonthlyVolume}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) => {
                      if (!selected) {
                        return <em className="text-gray-400">0 a 50</em>;
                      }
                      return selected;
                    },
                  }}
                  value={value ?? ''}
                  onChange={onChange}
                >
                  {PAYMENTS_FREQUENCY_OPTIONS_TRANSACTIONAL?.map((reason) => (
                    <MenuItem key={reason.id} value={t(reason.value)}>
                      {t(reason.value)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {errors.withdrawalMonthlyVolume && renderError()}
          </FormControl>
          <FormControl fullWidth className="!mb-4">
            <Typography className="!mb-2">{t('tpOperationsVolume')}</Typography>
            <Controller
              name="withdrawalMonthlyAmount"
              rules={{ required: true }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  select
                  id="withdrawalMonthlyAmount"
                  error={!!errors.withdrawalMonthlyAmount}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) => {
                      if (!selected) {
                        return (
                          <em className="text-gray-400">
                            $ 50,000.00 - $ 100,000.00
                          </em>
                        );
                      }
                      return selected;
                    },
                  }}
                  value={value ?? ''}
                  onChange={onChange}
                >
                  {PAYMENTS_AMOUNT_OPTIONS?.map((amount) => (
                    <MenuItem key={amount.id} value={t(amount.value)}>
                      {t(amount.value)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {errors.withdrawalMonthlyAmount && renderError()}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography className="!font-medium !mb-4" variant="h6">
            {t('tpExpenses')}
          </Typography>
          <FormControl fullWidth className="!mb-4">
            <Typography className="!mb-2">
              {t('tpTransactionsPerMonth')}
            </Typography>
            <Controller
              name="depositMonthlyVolume"
              rules={{ required: true }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  select
                  id="depositMonthlyVolume"
                  error={!!errors.depositMonthlyVolume}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) => {
                      if (!selected) {
                        return <em className="text-gray-400">0 a 50</em>;
                      }
                      return selected;
                    },
                  }}
                  value={value ?? ''}
                  onChange={onChange}
                >
                  {PAYMENTS_FREQUENCY_OPTIONS_TRANSACTIONAL?.map((reason) => (
                    <MenuItem key={reason.id} value={t(reason.value)}>
                      {t(reason.value)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {errors.depositMonthlyVolume && renderError()}
          </FormControl>
          <FormControl fullWidth className="!mb-4">
            <Typography className="!mb-2">{t('tpOperationsVolume')}</Typography>
            <Controller
              name="depositMonthlyAmount"
              rules={{ required: true }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  select
                  id="depositMonthlyAmount"
                  error={!!errors.depositMonthlyAmount}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) => {
                      if (!selected) {
                        return (
                          <em className="text-gray-400">
                            $ 50,000.00 - $ 100,000.00
                          </em>
                        );
                      }
                      return selected;
                    },
                  }}
                  value={value ?? ''}
                  onChange={onChange}
                >
                  {PAYMENTS_AMOUNT_OPTIONS?.map((amount) => (
                    <MenuItem key={amount.id} value={t(amount.value)}>
                      {t(amount.value)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {errors.depositMonthlyAmount && renderError()}
          </FormControl>
        </Grid>
        {error && (
          <Alert severity="error" className="!my-5">
            <AlertTitle>{t('errorRetryMessage')}</AlertTitle>

            <Typography>
              {t('tryOrContact')}{' '}
              <a className="font-bold" href={CONTACT_METHODS_LINK.SALES_EMAIL}>
                {CONTACT_METHODS_VALUES.SALES_EMAIL}
              </a>
            </Typography>
          </Alert>
        )}
        <Grid
          item
          xs={12}
          className="flex justify-center gap-10 max-w-[1000px] w-3/4"
        >
          {!isDisabledEdit ? (
            <>
              {prevStep && (
                <Button
                  size="large"
                  className="mt-2 text-[14px]"
                  type="button"
                  onClick={prevStep}
                  appearance="secondary"
                >
                  {t('backBtn')}
                </Button>
              )}
              <Button
                size="large"
                className="mt-2 bg-[#006BF8] border-[#006BF8] text-[14px]"
                type="submit"
                disabled={loading}
                appearance="primary"
              >
                {t('continue')}
              </Button>
            </>
          ) : (
            <Alert severity="info">{t('kybStepTwoTitle')}</Alert>
          )}
        </Grid>
      </form>
    </Box>
  );
};

export default TransactionalProfile;
